import { useState, type JSX } from 'react';
import {
  CREDENTIAL_KINDS,
  VNC_PASSWORD_EFFECTIVE_LENGTH,
  type Credential,
  type CredentialKind,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { Field } from '../../shared/ui/Field';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useUpdateCredential,
} from './credentials';

const KIND_LABEL_KEYS: Readonly<Record<CredentialKind, string>> = {
  vnc: 'credentials.kind.vnc',
  rtsp: 'credentials.kind.rtsp',
  snmp: 'credentials.kind.snmp',
  http_basic: 'credentials.kind.httpBasic',
  ssh: 'credentials.kind.ssh',
  ssh_key: 'credentials.kind.sshKey',
};

/**
 * Учётные данные устройств (§7.6, §10.2).
 *
 * Экран заведомо скучный, и это правильно: с секретом здесь можно сделать
 * ровно две вещи — заменить его целиком или удалить вместе с записью.
 * Посмотреть записанное нельзя (§3.3): секрет не отдаётся наружу ни одним
 * маршрутом, и поля «текущий пароль» здесь нет — его отсутствие и есть
 * требование.
 *
 * Кнопка-глаз у поля секрета показывает **набираемое**, а не хранимое.
 * Это не послабление: раскрывать нечего, значения нет ни в одном ответе
 * API. Пароль VNC вводят с бумажки, ошибаются в раскладке и в регистре,
 * а узнают об этом через минуту от сервера, отказавшего в подключении.
 */
export function CredentialsPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const credentials = useCredentials();
  const remove = useDeleteCredential();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Credential | null>(null);

  const columns: Column<Credential>[] = [
    {
      key: 'name',
      header: t('sites.form.name'),
      width: '1.6fr',
      sortValue: (row) => row.name,
      render: (row) => row.name,
    },
    {
      key: 'kind',
      header: t('notifications.column.kind'),
      width: '130px',
      sortValue: (row) => t(KIND_LABEL_KEYS[row.kind]),
      render: (row) => t(KIND_LABEL_KEYS[row.kind]),
    },
    {
      key: 'username',
      header: t('credentials.column.username'),
      width: '1fr',
      sortValue: (row) => row.username,
      render: (row) => (
        <span className="font-mono text-xs">{row.username ?? t('common.dash')}</span>
      ),
    },
    {
      key: 'used',
      header: t('credentials.column.nodes'),
      width: '90px',
      align: 'right',
      sortValue: (row) => row.usedByNodes,
      render: (row) => <span className="font-mono text-xs">{row.usedByNodes}</span>,
    },
    {
      key: 'created',
      header: t('credentials.column.createdAt'),
      width: '120px',
      sortValue: (row) => row.createdAt,
      render: (row) => (
        <span className="font-mono text-xs">{formatMoment(row.createdAt, language)}</span>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '72px',
      priority: 'actions',
      align: 'right',
      // Иконками без подписей: подписанные кнопки распирали таблицу шире
      // экрана. Имя для скринридера и подсказка при наведении остаются.
      render: (row) => (
        <span className="inline-flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon="pencil"
            aria-label={t('notifications.editAria', { name: row.name })}
            title={t('credentials.editTitle')}
            onClick={() => {
              setAdding(false);
              setEditing(row);
            }}
          />
          <Button
            size="sm"
            variant="danger"
            icon="trash"
            disabled={row.usedByNodes > 0 || remove.isPending}
            aria-label={t('notifications.deleteAria', { name: row.name })}
            title={
              row.usedByNodes > 0 ? t('credentials.detachFirst') : t('credentials.deleteTitle')
            }
            onClick={() => remove.mutate(row.id)}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      <Panel
        title={t('credentials.title')}
        subtitle={credentials.data ? `${String(credentials.data.length)}` : undefined}
        actions={
          <Button
            size="sm"
            icon="plus"
            onClick={() => {
              setEditing(null);
              setAdding((value) => !value);
            }}
          >
            {t('common.action.add')}
          </Button>
        }
      >
        {adding ? <CredentialForm onDone={() => setAdding(false)} /> : null}
        {editing ? (
          <CredentialForm key={editing.id} existing={editing} onDone={() => setEditing(null)} />
        ) : null}

        {credentials.isPending ? <Loading /> : null}

        {credentials.error ? (
          <ErrorState title={t('credentials.listFailed')} hint={errorText(credentials.error)} />
        ) : null}

        {remove.error ? (
          <ErrorState title={t('credentials.deleteFailed')} hint={errorText(remove.error)} />
        ) : null}

        {credentials.data ? (
          <DataTable
            columns={columns}
            rows={credentials.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title={t('credentials.emptyTitle')}
                hint={t('credentials.emptyHint')}
                action={
                  <Button icon="plus" onClick={() => setAdding(true)}>
                    {t('credentials.addPassword')}
                  </Button>
                }
              />
            }
          />
        ) : null}
      </Panel>
    </div>
  );
}

/**
 * Форма заведения и правки. Одна на оба случая: поля те же, различается
 * только судьба секрета.
 *
 * При правке секрет необязателен, и пустое поле означает «оставить прежний»,
 * а не «стереть». Стереть его нельзя вовсе: запись без секрета неотличима
 * от «пароль не задан», а это разные вещи — во втором случае надо отказать
 * в подключении, а не молча пробовать пустой.
 *
 * Вид при правке не меняется: он определяет, как секрет будет использован,
 * и смена вида у записи, уже привязанной к узлам, тихо сломала бы их
 * подключения. Нужен другой вид — заводится другая запись.
 */
function CredentialForm({
  existing,
  onDone,
}: {
  existing?: Credential;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const create = useCreateCredential();
  const update = useUpdateCredential();
  const [name, setName] = useState(existing?.name ?? '');
  const [kind, setKind] = useState<CredentialKind>(existing?.kind ?? 'vnc');
  const [username, setUsername] = useState(existing?.username ?? '');
  const [secret, setSecret] = useState('');

  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const truncated = kind === 'vnc' && secret.length > VNC_PASSWORD_EFFECTIVE_LENGTH;

  return (
    <form
      className="flex flex-col gap-3 border-b border-line px-3.5 py-3"
      onSubmit={(event) => {
        event.preventDefault();

        if (existing) {
          update.mutate(
            {
              id: existing.id,
              input: {
                name,
                username: kind === 'vnc' ? null : username,
                // Пустое поле — «оставить прежний»: секрет уходит на сервер,
                // только если его набрали заново.
                ...(secret.length > 0 ? { secret } : {}),
              },
            },
            { onSuccess: onDone },
          );
          return;
        }

        create.mutate(
          {
            name,
            kind,
            secret,
            ...(username.length > 0 ? { username } : {}),
          },
          {
            onSuccess: () => {
              setName('');
              setSecret('');
              setUsername('');
              onDone();
            },
          },
        );
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t('sites.form.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint={t('credentials.form.nameHint')}
          required
        />

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-dim">{t('notifications.column.kind')}</span>
          <select
            value={kind}
            disabled={existing !== undefined}
            title={existing ? t('credentials.form.kindFixed') : undefined}
            onChange={(event) => setKind(event.target.value as CredentialKind)}
            className="h-9 rounded-xs border border-line bg-bg px-2 text-sm disabled:opacity-50"
          >
            {CREDENTIAL_KINDS.map((value) => (
              <option key={value} value={value}>
                {t(KIND_LABEL_KEYS[value])}
              </option>
            ))}
          </select>
        </label>

        {kind === 'vnc' ? null : (
          <Field
            label={t('credentials.column.username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            hint={t('credentials.form.usernameHint')}
          />
        )}

        <Field
          label={existing ? t('credentials.form.newSecret') : t('credentials.form.secret')}
          type="password"
          reveal
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
          placeholder={existing ? t('credentials.form.leaveEmpty') : undefined}
          hint={
            truncated
              ? t('credentials.form.vncTruncatedHint')
              : existing
                ? t('credentials.form.keepSecretHint')
                : t('credentials.form.newSecretHint')
          }
          required={existing === undefined}
        />
      </div>

      {truncated ? (
        <p className="rounded-xs border-l-2 border-l-warn bg-overlay px-3 py-2 text-sm text-muted">
          {t('credentials.form.vncTruncatedNote', { limit: VNC_PASSWORD_EFFECTIVE_LENGTH })}
        </p>
      ) : null}

      {error ? <ErrorState title={t('notifications.saveFailed')} hint={errorText(error)} /> : null}

      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          disabled={saving || (existing === undefined && secret.length === 0)}
        >
          {saving ? t('sites.form.saving') : t('common.action.save')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('common.action.cancel')}
        </Button>
      </div>
    </form>
  );
}
